import { EntityManager } from "./EntityManager"
import type { EntityTarget } from "../common/EntityTarget"

import type { ObjectLiteral } from "../common/ObjectLiteral"
import type { MongoQueryRunner } from "../driver/mongodb/MongoQueryRunner"
import type { MongoDriver } from "../driver/mongodb/MongoDriver"
import { DocumentToEntityTransformer } from "../query-builder/transformer/DocumentToEntityTransformer"
import type { FindManyOptions } from "../find-options/FindManyOptions"
import { FindOptionsUtils } from "../find-options/FindOptionsUtils"
import { PlatformTools } from "../platform/PlatformTools"
import type { QueryDeepPartialEntity } from "../query-builder/QueryPartialEntity"
import { InsertResult } from "../query-builder/result/InsertResult"
import { UpdateResult } from "../query-builder/result/UpdateResult"
import { DeleteResult } from "../query-builder/result/DeleteResult"
import type { EntityMetadata } from "../metadata/EntityMetadata"
import { EntityPropertyNotFoundError } from "../error"

import type {
    AggregateOptions,
    AggregationCursor,
    AnyBulkWriteOperation,
    BulkWriteOptions,
    BulkWriteResult,
    ChangeStream,
    ChangeStreamOptions,
    Collection,
    CommandOperationOptions,
    CountDocumentsOptions,
    CountOptions,
    CreateIndexesOptions,
    DeleteOptions,
    DeleteResult as DeleteResultMongoDb,
    Document,
    Filter,
    FilterOperators,
    FindCursor,
    FindOneAndDeleteOptions,
    FindOneAndReplaceOptions,
    FindOneAndUpdateOptions,
    IndexDescription,
    IndexInformationOptions,
    IndexSpecification,
    InsertManyResult,
    InsertOneOptions,
    InsertOneResult,
    ListIndexesCursor,
    ListIndexesOptions,
    ObjectId,
    OptionalId,
    OrderedBulkOperation,
    RenameOptions,
    ReplaceOptions,
    UnorderedBulkOperation,
    UpdateFilter,
    UpdateOptions,
    UpdateResult as UpdateResultMongoDb,
} from "../driver/mongodb/typings"
import type { DataSource } from "../data-source/DataSource"
import type { MongoFindManyOptions } from "../find-options/mongodb/MongoFindManyOptions"
import type { MongoFindOneOptions } from "../find-options/mongodb/MongoFindOneOptions"
import type { FindOptionsSelect } from "../find-options/FindOptionsSelect"
import { ObjectUtils } from "../util/ObjectUtils"
import type { ColumnMetadata } from "../metadata/ColumnMetadata"

/**
 * Entity manager supposed to work with any entity, automatically find its repository and call its methods,
 * whatever entity type are you passing.
 *
 * This implementation is used for MongoDB driver which has some specifics in its EntityManager.
 */
export class MongoEntityManager extends EntityManager {
    readonly "@instanceof" = Symbol.for("MongoEntityManager")

    get mongoQueryRunner(): MongoQueryRunner {
        return (this.dataSource.driver as MongoDriver)
            .queryRunner as MongoQueryRunner
    }

    // -------------------------------------------------------------------------
    // Constructor
    // -------------------------------------------------------------------------

    constructor(dataSource: DataSource) {
        super(dataSource)
    }

    // -------------------------------------------------------------------------
    // Overridden Methods
    // -------------------------------------------------------------------------

    /**
     * Finds entities that match given find options.
     */
    /**
     * Finds entities that match given find options or conditions.
     *
     * @param entityClassOrName
     * @param optionsOrConditions
     */
    async find<Entity>(
        entityClassOrName: EntityTarget<Entity>,
        optionsOrConditions?:
            FindManyOptions<Entity> | Partial<Entity> | FilterOperators<Entity>,
    ): Promise<Entity[]> {
        const metadata = this.dataSource.getMetadata(entityClassOrName)
        const query = this.replaceObjectIdProperty(
            metadata,
            this.convertFindManyOptionsOrConditionsToMongodbQuery(
                optionsOrConditions,
            ),
        )
        const cursor = this.createEntityCursor<Entity>(
            entityClassOrName,
            query as Filter<Entity>,
        )
        const deleteDateColumn = metadata.deleteDateColumn
        if (FindOptionsUtils.isFindManyOptions(optionsOrConditions)) {
            if (optionsOrConditions.select)
                cursor.project(
                    this.convertFindOptionsSelectToProjectCriteria(
                        optionsOrConditions.select,
                        metadata,
                    ),
                )
            if (optionsOrConditions.skip) cursor.skip(optionsOrConditions.skip)
            if (optionsOrConditions.take) cursor.limit(optionsOrConditions.take)
            if (optionsOrConditions.order)
                cursor.sort(
                    this.convertFindOptionsOrderToOrderCriteria(
                        optionsOrConditions.order,
                    ),
                )
            if (deleteDateColumn && !optionsOrConditions.withDeleted) {
                this.filterSoftDeleted(cursor, deleteDateColumn, query)
            }
        } else if (deleteDateColumn) {
            this.filterSoftDeleted(cursor, deleteDateColumn, query)
        }
        return cursor.toArray()
    }

    /**
     * Finds entities that match given find options or conditions.
     * Also counts all entities that match given conditions,
     * but ignores pagination settings (from and take options).
     *
     * @param entityClassOrName
     * @param options
     */
    async findAndCount<Entity>(
        entityClassOrName: EntityTarget<Entity>,
        options?: MongoFindManyOptions<Entity>,
    ): Promise<[Entity[], number]> {
        return this.executeFindAndCount(entityClassOrName, options)
    }

    /**
     * Finds entities that match given where conditions.
     *
     * @param entityClassOrName
     * @param where
     */
    async findAndCountBy<Entity>(
        entityClassOrName: EntityTarget<Entity>,
        where: any,
    ): Promise<[Entity[], number]> {
        return this.executeFindAndCount(entityClassOrName, where)
    }

    /**
     * Finds entities that match given WHERE conditions.
     *
     * @param entityClassOrName
     * @param where
     */
    async findBy<Entity>(
        entityClassOrName: EntityTarget<Entity>,
        where: any,
    ): Promise<Entity[]> {
        return this.executeFind(entityClassOrName, where)
    }

    /**
     * Finds entities by ids.
     * Optionally find options can be applied.
     *
     * @param entityClassOrName
     * @param ids
     * @param optionsOrConditions
     */
    async findByIds<Entity>(
        entityClassOrName: EntityTarget<Entity>,
        ids: any[],
        optionsOrConditions?: FindManyOptions<Entity> | Partial<Entity>,
    ): Promise<Entity[]> {
        const metadata = this.dataSource.getMetadata(entityClassOrName)
        const query =
            this.convertFindManyOptionsOrConditionsToMongodbQuery(
                optionsOrConditions,
            ) ?? {}
        const objectIdClass = PlatformTools.load("mongodb").ObjectId
        query["_id"] = {
            $in: ids.map((id) => {
                if (typeof id === "string") {
                    return new objectIdClass(id)
                }

                if (typeof id === "object") {
                    if (id instanceof objectIdClass) {
                        return id
                    }

                    const propertyName = metadata.objectIdColumn!.propertyName

                    if (id[propertyName] instanceof objectIdClass) {
                        return id[propertyName]
                    }
                }
            }),
        }

        const cursor = this.createEntityCursor<Entity>(
            entityClassOrName,
            query as Filter<Entity>,
        )
        if (FindOptionsUtils.isFindManyOptions(optionsOrConditions)) {
            if (optionsOrConditions.select)
                cursor.project(
                    this.convertFindOptionsSelectToProjectCriteria(
                        optionsOrConditions.select,
                        metadata,
                    ),
                )
            if (optionsOrConditions.skip) cursor.skip(optionsOrConditions.skip)
            if (optionsOrConditions.take) cursor.limit(optionsOrConditions.take)
            if (optionsOrConditions.order)
                cursor.sort(
                    this.convertFindOptionsOrderToOrderCriteria(
                        optionsOrConditions.order,
                    ),
                )
        }
        return cursor.toArray()
    }

    /**
     * Finds first entity that matches given conditions and/or find options.
     *
     * @param entityClassOrName
     * @param options
     */
    async findOne<Entity>(
        entityClassOrName: EntityTarget<Entity>,
        options: MongoFindOneOptions<Entity>,
    ): Promise<Entity | null> {
        return this.executeFindOne(entityClassOrName, options)
    }

    /**
     * Finds first entity that matches given WHERE conditions.
     *
     * @param entityClassOrName
     * @param where
     */
    async findOneBy<Entity>(
        entityClassOrName: EntityTarget<Entity>,
        where: any,
    ): Promise<Entity | null> {
        return this.executeFindOne(entityClassOrName, where)
    }

    /**
     * Inserts a given entity into the database.
     * Unlike save method executes a primitive operation without cascades, relations and other operations included.
     * Executes fast and efficient INSERT query.
     * Does not check if entity exist in the database, so query will fail if duplicate entity is being inserted.
     * You can execute bulk inserts using this method.
     *
     * @param target
     * @param entity
     */
    async insert<Entity>(
        target: EntityTarget<Entity>,
        entity:
            QueryDeepPartialEntity<Entity> | QueryDeepPartialEntity<Entity>[],
    ): Promise<InsertResult> {
        // todo: convert entity to its database name
        const result = new InsertResult()
        if (Array.isArray(entity)) {
            result.raw = await this.insertMany(target, entity)
            Object.keys(result.raw.insertedIds).forEach((key: any) => {
                const insertedId = result.raw.insertedIds[key]
                result.generatedMaps.push(
                    this.dataSource.driver.createGeneratedMap(
                        this.dataSource.getMetadata(target),
                        insertedId,
                    )!,
                )
                result.identifiers.push(
                    this.dataSource.driver.createGeneratedMap(
                        this.dataSource.getMetadata(target),
                        insertedId,
                    )!,
                )
            })
        } else {
            result.raw = await this.insertOne(target, entity)
            result.generatedMaps.push(
                this.dataSource.driver.createGeneratedMap(
                    this.dataSource.getMetadata(target),
                    result.raw.insertedId,
                )!,
            )
            result.identifiers.push(
                this.dataSource.driver.createGeneratedMap(
                    this.dataSource.getMetadata(target),
                    result.raw.insertedId,
                )!,
            )
        }

        return result
    }

    /**
     * Updates entity partially. Entity can be found by a given conditions.
     * Unlike save method executes a primitive operation without cascades, relations and other operations included.
     * Executes fast and efficient UPDATE query.
     * Does not check if entity exist in the database.
     *
     * @param target
     * @param criteria
     * @param partialEntity
     */
    async update<Entity>(
        target: EntityTarget<Entity>,
        criteria:
            | string
            | string[]
            | number
            | number[]
            | Date
            | Date[]
            | ObjectId
            | ObjectId[]
            | ObjectLiteral,
        partialEntity: QueryDeepPartialEntity<Entity>,
    ): Promise<UpdateResult> {
        const result = new UpdateResult()

        if (Array.isArray(criteria)) {
            const updateResults = await Promise.all(
                (criteria as any[]).map((criteriaItem) => {
                    return this.update(target, criteriaItem, partialEntity)
                }),
            )

            result.raw = updateResults.map((r) => r.raw)
            result.affected = updateResults
                .map((r) => r.affected ?? 0)
                .reduce((c, r) => c + r, 0)
            result.generatedMaps = updateResults.reduce(
                (c, r) => c.concat(r.generatedMaps),
                [] as ObjectLiteral[],
            )
        } else {
            const metadata = this.dataSource.getMetadata(target)
            const mongoResult = await this.updateMany(
                target,
                this.convertMixedCriteria(metadata, criteria),
                { $set: partialEntity },
            )

            result.raw = mongoResult
            result.affected = mongoResult.modifiedCount
        }

        return result
    }

    /**
     * Deletes entities by a given conditions.
     * Unlike save method executes a primitive operation without cascades, relations and other operations included.
     * Executes fast and efficient DELETE query.
     * Does not check if entity exist in the database.
     *
     * @param target
     * @param criteria
     */
    async delete<Entity>(
        target: EntityTarget<Entity>,
        criteria:
            | string
            | string[]
            | number
            | number[]
            | Date
            | Date[]
            | ObjectId
            | ObjectId[]
            | ObjectLiteral[],
    ): Promise<DeleteResult> {
        const result = new DeleteResult()

        if (Array.isArray(criteria)) {
            const deleteResults = await Promise.all(
                (criteria as any[]).map((criteriaItem) => {
                    return this.delete(target, criteriaItem)
                }),
            )

            result.raw = deleteResults.map((r) => r.raw)
            result.affected = deleteResults
                .map((r) => r.affected ?? 0)
                .reduce((c, r) => c + r, 0)
        } else {
            const mongoResult = await this.deleteMany(
                target,
                this.convertMixedCriteria(
                    this.dataSource.getMetadata(target),
                    criteria,
                ),
            )

            result.raw = mongoResult
            result.affected = mongoResult.deletedCount
        }

        return result
    }

    // -------------------------------------------------------------------------
    // Public Methods
    // -------------------------------------------------------------------------

    /**
     * Creates a cursor for a query that can be used to iterate over results from MongoDB.
     *
     * @param entityClassOrName
     * @param query
     */
    createCursor<Entity, T = any>(
        entityClassOrName: EntityTarget<Entity>,
        query: ObjectLiteral = {},
    ): FindCursor<T> {
        const metadata = this.dataSource.getMetadata(entityClassOrName)
        return this.mongoQueryRunner.cursor(metadata.tableName, query)
    }

    /**
     * Creates a cursor for a query that can be used to iterate over results from MongoDB.
     * This returns modified version of cursor that transforms each result into Entity model.
     *
     * @param entityClassOrName
     * @param query
     */
    createEntityCursor<Entity>(
        entityClassOrName: EntityTarget<Entity>,
        query: ObjectLiteral = {},
    ): FindCursor<Entity> {
        const metadata = this.dataSource.getMetadata(entityClassOrName)
        const cursor = this.createCursor(entityClassOrName, query)
        this.applyEntityTransformationToCursor(metadata, cursor)
        return cursor
    }

    /**
     * Execute an aggregation framework pipeline against the collection.
     *
     * @param entityClassOrName
     * @param pipeline
     * @param options
     */
    aggregate<Entity, R = any>(
        entityClassOrName: EntityTarget<Entity>,
        pipeline: Document[],
        options?: AggregateOptions,
    ): AggregationCursor<R> {
        const metadata = this.dataSource.getMetadata(entityClassOrName)
        return this.mongoQueryRunner.aggregate(
            metadata.tableName,
            pipeline,
            options,
        )
    }

    /**
     * Execute an aggregation framework pipeline against the collection.
     * This returns modified version of cursor that transforms each result into Entity model.
     *
     * @param entityClassOrName
     * @param pipeline
     * @param options
     */
    aggregateEntity<Entity>(
        entityClassOrName: EntityTarget<Entity>,
        pipeline: Document[],
        options?: AggregateOptions,
    ): AggregationCursor<Entity> {
        const metadata = this.dataSource.getMetadata(entityClassOrName)
        const cursor = this.mongoQueryRunner.aggregate(
            metadata.tableName,
            pipeline,
            options,
        )
        this.applyEntityTransformationToCursor(metadata, cursor)
        return cursor
    }

    /**
     * Perform a bulkWrite operation without a fluent API.
     *
     * @param entityClassOrName
     * @param operations
     * @param options
     */
    bulkWrite<Entity>(
        entityClassOrName: EntityTarget<Entity>,
        operations: AnyBulkWriteOperation<Document>[],
        options?: BulkWriteOptions,
    ): Promise<BulkWriteResult> {
        const metadata = this.dataSource.getMetadata(entityClassOrName)
        return this.mongoQueryRunner.bulkWrite(
            metadata.tableName,
            operations,
            options,
        )
    }

    /**
     * Count number of matching documents in the db to a query.
     *
     * @param entityClassOrName
     * @param query
     * @param options
     */
    count<Entity>(
        entityClassOrName: EntityTarget<Entity>,
        query: Filter<Document> = {},
        options: CountOptions = {},
    ): Promise<number> {
        const metadata = this.dataSource.getMetadata(entityClassOrName)
        return this.mongoQueryRunner.count(metadata.tableName, query, options)
    }

    /**
     * Count number of matching documents in the db to a query.
     *
     * @param entityClassOrName
     * @param query
     * @param options
     */
    countDocuments<Entity>(
        entityClassOrName: EntityTarget<Entity>,
        query: Filter<Document> = {},
        options: CountDocumentsOptions = {},
    ): Promise<number> {
        const metadata = this.dataSource.getMetadata(entityClassOrName)
        return this.mongoQueryRunner.countDocuments(
            metadata.tableName,
            query,
            options,
        )
    }

    /**
     * Count number of matching documents in the db to a query.
     *
     * @param entityClassOrName
     * @param query
     * @param options
     */
    countBy<Entity>(
        entityClassOrName: EntityTarget<Entity>,
        query?: ObjectLiteral,
        options?: CountOptions,
    ): Promise<number> {
        return this.count(entityClassOrName, query, options)
    }

    /**
     * Creates an index on the db and collection.
     *
     * @param entityClassOrName
     * @param fieldOrSpec
     * @param options
     */
    createCollectionIndex<Entity>(
        entityClassOrName: EntityTarget<Entity>,
        fieldOrSpec: IndexSpecification,
        options?: CreateIndexesOptions,
    ): Promise<string> {
        const metadata = this.dataSource.getMetadata(entityClassOrName)
        return this.mongoQueryRunner.createCollectionIndex(
            metadata.tableName,
            fieldOrSpec,
            options,
        )
    }

    /**
     * Creates multiple indexes in the collection, this method is only supported for MongoDB 2.6 or higher.
     * Earlier version of MongoDB will throw a command not supported error.
     * Index specifications are defined at http://docs.mongodb.org/manual/reference/command/createIndexes/.
     *
     * @param entityClassOrName
     * @param indexSpecs
     */
    createCollectionIndexes<Entity>(
        entityClassOrName: EntityTarget<Entity>,
        indexSpecs: IndexDescription[],
    ): Promise<string[]> {
        const metadata = this.dataSource.getMetadata(entityClassOrName)
        return this.mongoQueryRunner.createCollectionIndexes(
            metadata.tableName,
            indexSpecs,
        )
    }

    /**
     * Delete multiple documents on MongoDB.
     *
     * @param entityClassOrName
     * @param query
     * @param options
     */
    deleteMany<Entity>(
        entityClassOrName: EntityTarget<Entity>,
        query: Filter<Document>,
        options: DeleteOptions = {},
    ): Promise<DeleteResultMongoDb> {
        const metadata = this.dataSource.getMetadata(entityClassOrName)
        return this.mongoQueryRunner.deleteMany(
            metadata.tableName,
            query,
            options,
        )
    }

    /**
     * Delete a document on MongoDB.
     *
     * @param entityClassOrName
     * @param query
     * @param options
     */
    deleteOne<Entity>(
        entityClassOrName: EntityTarget<Entity>,
        query: Filter<Document>,
        options: DeleteOptions = {},
    ): Promise<DeleteResultMongoDb> {
        const metadata = this.dataSource.getMetadata(entityClassOrName)
        return this.mongoQueryRunner.deleteOne(
            metadata.tableName,
            query,
            options,
        )
    }

    /**
     * The distinct command returns returns a list of distinct values for the given key across a collection.
     *
     * @param entityClassOrName
     * @param key
     * @param query
     * @param options
     */
    distinct<Entity>(
        entityClassOrName: EntityTarget<Entity>,
        key: string,
        query: Filter<Document>,
        options?: CommandOperationOptions,
    ): Promise<any> {
        const metadata = this.dataSource.getMetadata(entityClassOrName)
        return this.mongoQueryRunner.distinct(
            metadata.tableName,
            key,
            query,
            options,
        )
    }

    /**
     * Drops an index from this collection.
     *
     * @param entityClassOrName
     * @param indexName
     * @param options
     */
    dropCollectionIndex<Entity>(
        entityClassOrName: EntityTarget<Entity>,
        indexName: string,
        options?: CommandOperationOptions,
    ): Promise<any> {
        const metadata = this.dataSource.getMetadata(entityClassOrName)
        return this.mongoQueryRunner.dropCollectionIndex(
            metadata.tableName,
            indexName,
            options,
        )
    }

    /**
     * Drops all indexes from the collection.
     *
     * @param entityClassOrName
     */
    dropCollectionIndexes<Entity>(
        entityClassOrName: EntityTarget<Entity>,
    ): Promise<any> {
        const metadata = this.dataSource.getMetadata(entityClassOrName)
        return this.mongoQueryRunner.dropCollectionIndexes(metadata.tableName)
    }

    /**
     * Find a document and delete it in one atomic operation, requires a write lock for the duration of the operation.
     *
     * @param entityClassOrName
     * @param query
     * @param options
     */
    findOneAndDelete<Entity>(
        entityClassOrName: EntityTarget<Entity>,
        query: ObjectLiteral,
        options?: FindOneAndDeleteOptions,
    ): Promise<Document | null> {
        const metadata = this.dataSource.getMetadata(entityClassOrName)
        return this.mongoQueryRunner.findOneAndDelete(
            metadata.tableName,
            query,
            options,
        )
    }

    /**
     * Find a document and replace it in one atomic operation, requires a write lock for the duration of the operation.
     *
     * @param entityClassOrName
     * @param query
     * @param replacement
     * @param options
     */
    findOneAndReplace<Entity>(
        entityClassOrName: EntityTarget<Entity>,
        query: Filter<Document>,
        replacement: Document,
        options?: FindOneAndReplaceOptions,
    ): Promise<Document | null> {
        const metadata = this.dataSource.getMetadata(entityClassOrName)
        return this.mongoQueryRunner.findOneAndReplace(
            metadata.tableName,
            query,
            replacement,
            options,
        )
    }

    /**
     * Find a document and update it in one atomic operation, requires a write lock for the duration of the operation.
     *
     * @param entityClassOrName
     * @param query
     * @param update
     * @param options
     */
    findOneAndUpdate<Entity>(
        entityClassOrName: EntityTarget<Entity>,
        query: Filter<Document>,
        update: UpdateFilter<Document>,
        options?: FindOneAndUpdateOptions,
    ): Promise<Document | null> {
        const metadata = this.dataSource.getMetadata(entityClassOrName)
        return this.mongoQueryRunner.findOneAndUpdate(
            metadata.tableName,
            query,
            update,
            options,
        )
    }

    /**
     * Retrieve all the indexes on the collection.
     *
     * @param entityClassOrName
     */
    collectionIndexes<Entity>(
        entityClassOrName: EntityTarget<Entity>,
    ): Promise<Document> {
        const metadata = this.dataSource.getMetadata(entityClassOrName)
        return this.mongoQueryRunner.collectionIndexes(metadata.tableName)
    }

    /**
     * Retrieve all the indexes on the collection.
     *
     * @param entityClassOrName
     * @param indexes
     */
    collectionIndexExists<Entity>(
        entityClassOrName: EntityTarget<Entity>,
        indexes: string | string[],
    ): Promise<boolean> {
        const metadata = this.dataSource.getMetadata(entityClassOrName)
        return this.mongoQueryRunner.collectionIndexExists(
            metadata.tableName,
            indexes,
        )
    }

    /**
     * Retrieves this collections index info.
     *
     * @param entityClassOrName
     * @param options
     */
    collectionIndexInformation<Entity>(
        entityClassOrName: EntityTarget<Entity>,
        options?: IndexInformationOptions,
    ): Promise<any> {
        const metadata = this.dataSource.getMetadata(entityClassOrName)
        return this.mongoQueryRunner.collectionIndexInformation(
            metadata.tableName,
            options,
        )
    }

    /**
     * Initiate an In order bulk write operation, operations will be serially executed in the order they are added, creating a new operation for each switch in types.
     *
     * @param entityClassOrName
     * @param options
     */
    initializeOrderedBulkOp<Entity>(
        entityClassOrName: EntityTarget<Entity>,
        options?: BulkWriteOptions,
    ): OrderedBulkOperation {
        const metadata = this.dataSource.getMetadata(entityClassOrName)
        return this.mongoQueryRunner.initializeOrderedBulkOp(
            metadata.tableName,
            options,
        )
    }

    /**
     * Initiate a Out of order batch write operation. All operations will be buffered into insert/update/remove commands executed out of order.
     *
     * @param entityClassOrName
     * @param options
     */
    initializeUnorderedBulkOp<Entity>(
        entityClassOrName: EntityTarget<Entity>,
        options?: BulkWriteOptions,
    ): UnorderedBulkOperation {
        const metadata = this.dataSource.getMetadata(entityClassOrName)
        return this.mongoQueryRunner.initializeUnorderedBulkOp(
            metadata.tableName,
            options,
        )
    }

    /**
     * Inserts an array of documents into MongoDB.
     *
     * @param entityClassOrName
     * @param docs
     * @param options
     */
    insertMany<Entity>(
        entityClassOrName: EntityTarget<Entity>,
        docs: OptionalId<Document>[],
        options?: BulkWriteOptions,
    ): Promise<InsertManyResult> {
        const metadata = this.dataSource.getMetadata(entityClassOrName)
        return this.mongoQueryRunner.insertMany(
            metadata.tableName,
            docs,
            options,
        )
    }

    /**
     * Inserts a single document into MongoDB.
     *
     * @param entityClassOrName
     * @param doc
     * @param options
     */
    insertOne<Entity>(
        entityClassOrName: EntityTarget<Entity>,
        doc: OptionalId<Document>,
        options?: InsertOneOptions,
    ): Promise<InsertOneResult> {
        const metadata = this.dataSource.getMetadata(entityClassOrName)
        return this.mongoQueryRunner.insertOne(metadata.tableName, doc, options)
    }

    /**
     * Returns if the collection is a capped collection.
     *
     * @param entityClassOrName
     */
    isCapped<Entity>(entityClassOrName: EntityTarget<Entity>): Promise<any> {
        const metadata = this.dataSource.getMetadata(entityClassOrName)
        return this.mongoQueryRunner.isCapped(metadata.tableName)
    }

    /**
     * Get the list of all indexes information for the collection.
     *
     * @param entityClassOrName
     * @param options
     */
    listCollectionIndexes<Entity>(
        entityClassOrName: EntityTarget<Entity>,
        options?: ListIndexesOptions,
    ): ListIndexesCursor {
        const metadata = this.dataSource.getMetadata(entityClassOrName)
        return this.mongoQueryRunner.listCollectionIndexes(
            metadata.tableName,
            options,
        )
    }

    /**
     * Reindex all indexes on the collection Warning: reIndex is a blocking operation (indexes are rebuilt in the foreground) and will be slow for large collections.
     *
     * @param entityClassOrName
     * @param newName
     * @param options
     */
    rename<Entity>(
        entityClassOrName: EntityTarget<Entity>,
        newName: string,
        options?: RenameOptions,
    ): Promise<Collection<Document>> {
        const metadata = this.dataSource.getMetadata(entityClassOrName)
        return this.mongoQueryRunner.rename(
            metadata.tableName,
            newName,
            options,
        )
    }

    /**
     * Replace a document on MongoDB.
     *
     * @param entityClassOrName
     * @param query
     * @param doc
     * @param options
     */
    replaceOne<Entity>(
        entityClassOrName: EntityTarget<Entity>,
        query: Filter<Document>,
        doc: Document,
        options?: ReplaceOptions,
    ): Promise<Document | UpdateResultMongoDb> {
        const metadata = this.dataSource.getMetadata(entityClassOrName)
        return this.mongoQueryRunner.replaceOne(
            metadata.tableName,
            query,
            doc,
            options,
        )
    }

    watch<Entity>(
        entityClassOrName: EntityTarget<Entity>,
        pipeline?: Document[],
        options?: ChangeStreamOptions,
    ): ChangeStream {
        const metadata = this.dataSource.getMetadata(entityClassOrName)
        return this.mongoQueryRunner.watch(
            metadata.tableName,
            pipeline,
            options,
        )
    }

    /**
     * Update multiple documents on MongoDB.
     *
     * @param entityClassOrName
     * @param query
     * @param update
     * @param options
     */
    updateMany<Entity>(
        entityClassOrName: EntityTarget<Entity>,
        query: Filter<Document>,
        update: UpdateFilter<Document>,
        options?: UpdateOptions,
    ): Promise<Document | UpdateResultMongoDb> {
        const metadata = this.dataSource.getMetadata(entityClassOrName)
        return this.mongoQueryRunner.updateMany(
            metadata.tableName,
            query,
            update,
            options,
        )
    }

    /**
     * Update a single document on MongoDB.
     *
     * @param entityClassOrName
     * @param query
     * @param update
     * @param options
     */
    updateOne<Entity>(
        entityClassOrName: EntityTarget<Entity>,
        query: Filter<Document>,
        update: UpdateFilter<Document>,
        options?: UpdateOptions,
    ): Promise<Document | UpdateResultMongoDb> {
        const metadata = this.dataSource.getMetadata(entityClassOrName)
        return this.mongoQueryRunner.updateOne(
            metadata.tableName,
            query,
            update,
            options,
        )
    }

    // -------------------------------------------------------------------------
    // Protected Methods
    // -------------------------------------------------------------------------

    /**
     * Replaces the entity's ObjectId property name (e.g. "id") with "_id" in a
     * query object so that `findOneBy({ id: value })` works as expected.
     *
     * @param metadata
     * @param query
     */
    protected replaceObjectIdProperty(
        metadata: EntityMetadata,
        query: ObjectLiteral | undefined,
    ): ObjectLiteral | undefined {
        if (!query) return query

        const objectIdColumn = metadata.objectIdColumn
        if (!objectIdColumn) return query

        const propertyName = objectIdColumn.propertyName
        if (propertyName === "_id") return query

        if (!(propertyName in query)) {
            const hasNested =
                ("$or" in query && Array.isArray(query.$or)) ||
                ("$and" in query && Array.isArray(query.$and))
            if (!hasNested) return query
        }

        const objectIdClass = PlatformTools.load("mongodb").ObjectId
        return this.rewriteObjectIdQuery(query, propertyName, objectIdClass)
    }

    /**
     * Recursively rewrites a query object, renaming the given property to
     * "_id" and converting values to ObjectId instances. Walks into $or/$and.
     *
     * @param obj
     * @param propertyName
     * @param objectIdClass
     */
    private rewriteObjectIdQuery(
        obj: ObjectLiteral,
        propertyName: string,
        objectIdClass: any,
    ): ObjectLiteral {
        const result: ObjectLiteral = {}
        for (const [key, value] of Object.entries(obj)) {
            if (key === propertyName) {
                result["_id"] = this.convertToObjectId(value, objectIdClass)
            } else if (
                (key === "$or" || key === "$and") &&
                Array.isArray(value)
            ) {
                result[key] = value.map((item: any) =>
                    typeof item === "object" && item !== null
                        ? this.rewriteObjectIdQuery(
                              item,
                              propertyName,
                              objectIdClass,
                          )
                        : item,
                )
            } else {
                result[key] = value
            }
        }
        return result
    }

    /**
     * Converts a query value to ObjectId, handling scalars, arrays, and
     * MongoDB operator objects (e.g. { $in: [...] }, { $ne: ... }).
     *
     * @param value
     * @param objectIdClass
     */
    private convertToObjectId(value: any, objectIdClass: any): any {
        if (value instanceof objectIdClass) return value
        if (typeof value === "string" || typeof value === "number")
            return new objectIdClass(value)
        if (Array.isArray(value))
            return value.map((v) => this.convertToObjectId(v, objectIdClass))
        if (
            value !== null &&
            typeof value === "object" &&
            Object.keys(value).some((k) => k.startsWith("$"))
        ) {
            const result: ObjectLiteral = {}
            for (const [k, v] of Object.entries(value)) {
                result[k] = this.convertToObjectId(v, objectIdClass)
            }
            return result
        }
        return value
    }

    /**
     * Converts FindManyOptions to mongodb query.
     *
     * @param optionsOrConditions
     */
    protected convertFindManyOptionsOrConditionsToMongodbQuery<Entity>(
        optionsOrConditions:
            | MongoFindManyOptions<Entity>
            | Partial<Entity>
            | FilterOperators<Entity>
            | any[]
            | undefined,
    ): ObjectLiteral | undefined {
        if (!optionsOrConditions) return undefined

        FindOptionsUtils.rejectJoinOption(optionsOrConditions)
        FindOptionsUtils.rejectStringArraySelect(optionsOrConditions)
        FindOptionsUtils.rejectStringArrayRelations(optionsOrConditions)

        if (FindOptionsUtils.isFindManyOptions<Entity>(optionsOrConditions))
            // If where condition is passed as a string which contains sql we have to ignore
            // as mongo is not a sql database
            return typeof optionsOrConditions.where === "string"
                ? {}
                : optionsOrConditions.where

        return optionsOrConditions
    }

    /**
     * Converts FindOneOptions to mongodb query.
     *
     * @param optionsOrConditions
     */
    protected convertFindOneOptionsOrConditionsToMongodbQuery<Entity>(
        optionsOrConditions:
            MongoFindOneOptions<Entity> | Partial<Entity> | undefined,
    ): ObjectLiteral | undefined {
        if (!optionsOrConditions) return undefined

        FindOptionsUtils.rejectJoinOption(optionsOrConditions)
        FindOptionsUtils.rejectStringArraySelect(optionsOrConditions)
        FindOptionsUtils.rejectStringArrayRelations(optionsOrConditions)

        if (FindOptionsUtils.isFindOneOptions<Entity>(optionsOrConditions))
            // If where condition is passed as a string which contains sql we have to ignore
            // as mongo is not a sql database
            return typeof optionsOrConditions.where === "string"
                ? {}
                : optionsOrConditions.where

        return optionsOrConditions
    }

    /**
     * Converts FindOptions into mongodb order by criteria.
     *
     * @param order
     */
    protected convertFindOptionsOrderToOrderCriteria(order: ObjectLiteral) {
        return Object.keys(order).reduce((orderCriteria, key) => {
            switch (order[key]) {
                case "DESC":
                    orderCriteria[key] = -1
                    break
                case "ASC":
                    orderCriteria[key] = 1
                    break
                default:
                    orderCriteria[key] = order[key]
            }
            return orderCriteria
        }, {} as ObjectLiteral)
    }

    /**
     * Converts FindOptions into mongodb select by criteria.
     *
     * @param selects
     * @param metadata
     */
    protected convertFindOptionsSelectToProjectCriteria(
        selects: FindOptionsSelect<any>,
        metadata: EntityMetadata,
    ) {
        const projection: ObjectLiteral = {}
        const build = (obj: ObjectLiteral, embedPrefix: string) => {
            for (const key of Object.keys(obj)) {
                const value = obj[key]
                if (value === undefined || value === false) continue

                const propertyPath = embedPrefix ? `${embedPrefix}.${key}` : key

                if (metadata.findColumnWithPropertyPathStrict(propertyPath)) {
                    projection[propertyPath] = 1
                    continue
                }

                const embed =
                    metadata.findEmbeddedWithPropertyPath(propertyPath)
                if (embed) {
                    if (value === true) {
                        for (const subColumn of embed.columnsFromTree) {
                            projection[subColumn.propertyPath] = 1
                        }
                    } else if (typeof value === "object") {
                        build(value as ObjectLiteral, propertyPath)
                    }
                    continue
                }

                if (metadata.findRelationWithPropertyPath(propertyPath))
                    continue

                throw new EntityPropertyNotFoundError(propertyPath, metadata)
            }
        }
        build(selects as ObjectLiteral, "")

        // Translate ObjectIdColumn property name (e.g. "id") to "_id" for MongoDB
        if (metadata.objectIdColumn) {
            const propertyName = metadata.objectIdColumn.propertyName
            if (
                propertyName !== "_id" &&
                projection[propertyName] !== undefined
            ) {
                projection["_id"] = projection[propertyName]
                delete projection[propertyName]
            }
        }

        return projection
    }

    /**
     * Ensures given id is an id for query.
     *
     * @param metadata
     * @param idMap
     */
    protected convertMixedCriteria(
        metadata: EntityMetadata,
        idMap: any,
    ): ObjectLiteral {
        const objectIdInstance = PlatformTools.load("mongodb").ObjectId

        // check first if it's ObjectId compatible:
        // string, number, Buffer, ObjectId or ObjectId-like
        if (objectIdInstance.isValid(idMap)) {
            return {
                _id: new objectIdInstance(idMap),
            }
        }

        // if it's some other type of object build a query from the columns
        // this check needs to be after the ObjectId check, because a valid ObjectId is also an Object instance
        if (ObjectUtils.isObject(idMap)) {
            return metadata.columns.reduce((query, column) => {
                const columnValue = column.getEntityValue(idMap)
                if (columnValue !== undefined)
                    query[column.databasePath] = columnValue
                return query
            }, {} as any)
        }

        // last resort: try to convert it to an ObjectId anyway
        // most likely it will fail, but we want to be backwards compatible and keep the same thrown Errors.
        // it can still pass with null/undefined
        return {
            _id: new objectIdInstance(idMap),
        }
    }

    /**
     * Overrides cursor's toArray and next methods to convert results to entity automatically.
     *
     * @param metadata
     * @param cursor
     */
    protected applyEntityTransformationToCursor<Entity extends ObjectLiteral>(
        metadata: EntityMetadata,
        cursor: FindCursor<Entity> | AggregationCursor<Entity>,
    ) {
        const transformer = new DocumentToEntityTransformer()
        const broadcaster = this.mongoQueryRunner.broadcaster
        let isInToArray = false

        // transformer function that converts raw document to entity
        // used by both next and toArray internally in the MongoDB driver
        cursor.transform = (doc: ObjectLiteral) => {
            return transformer.transform(doc, metadata)
        }

        // override toArray for batch broadcast (skip per-doc broadcast in next)
        const originalToArray = cursor.toArray.bind(cursor)
        cursor.toArray = async () => {
            isInToArray = true
            try {
                const entities = await originalToArray()
                if (entities.length > 0) {
                    await broadcaster.broadcast("Load", metadata, entities)
                }
                return entities
            } finally {
                isInToArray = false
            }
        }

        // override next for per-doc broadcast (skip if in toArray batch)
        const originalNext = cursor.next.bind(cursor)
        cursor.next = async () => {
            const entity = await originalNext()
            if (entity && !isInToArray) {
                await broadcaster.broadcast("Load", metadata, [entity])
            }
            return entity
        }
    }

    protected filterSoftDeleted<Entity>(
        cursor: FindCursor<Entity>,
        deleteDateColumn: ColumnMetadata,
        query?: ObjectLiteral,
    ) {
        const { $or, ...restQuery } = query ?? {}
        cursor.filter({
            $or: [
                { [deleteDateColumn.propertyName]: { $eq: null } },
                ...(Array.isArray($or) ? $or : []),
            ],
            ...restQuery,
        })
    }

    /**
     * Finds first entity that matches given conditions and/or find options.
     *
     * @param entityClassOrName
     * @param optionsOrConditions
     * @param maybeOptions
     */
    protected async executeFindOne<Entity>(
        entityClassOrName: EntityTarget<Entity>,
        optionsOrConditions?: any,
        maybeOptions?: MongoFindOneOptions<Entity>,
    ): Promise<Entity | null> {
        const objectIdClass = PlatformTools.load("mongodb").ObjectId
        const id =
            optionsOrConditions instanceof objectIdClass ||
            typeof optionsOrConditions === "string"
                ? optionsOrConditions
                : undefined
        const findOneOptionsOrConditions = (
            id ? maybeOptions : optionsOrConditions
        ) as any
        const metadata = this.dataSource.getMetadata(entityClassOrName)
        const query =
            this.replaceObjectIdProperty(
                metadata,
                this.convertFindOneOptionsOrConditionsToMongodbQuery(
                    findOneOptionsOrConditions,
                ),
            ) ?? {}
        if (id) {
            query["_id"] =
                id instanceof objectIdClass ? id : new objectIdClass(id)
        }
        const cursor = this.createEntityCursor<Entity>(entityClassOrName, query)
        const deleteDateColumn =
            this.dataSource.getMetadata(entityClassOrName).deleteDateColumn
        if (FindOptionsUtils.isFindOneOptions(findOneOptionsOrConditions)) {
            if (findOneOptionsOrConditions.select)
                cursor.project(
                    this.convertFindOptionsSelectToProjectCriteria(
                        findOneOptionsOrConditions.select,
                        metadata,
                    ),
                )
            if (findOneOptionsOrConditions.order)
                cursor.sort(
                    this.convertFindOptionsOrderToOrderCriteria(
                        findOneOptionsOrConditions.order,
                    ),
                )
            if (deleteDateColumn && !findOneOptionsOrConditions.withDeleted) {
                this.filterSoftDeleted(cursor, deleteDateColumn, query)
            }
        } else if (deleteDateColumn) {
            this.filterSoftDeleted(cursor, deleteDateColumn, query)
        }

        // const result = await cursor.limit(1).next();
        const result = await cursor.limit(1).toArray()
        return result.length > 0 ? result[0] : null
    }

    protected async executeFind<Entity>(
        entityClassOrName: EntityTarget<Entity>,
        optionsOrConditions?:
            MongoFindManyOptions<Entity> | Partial<Entity> | any[],
    ): Promise<Entity[]> {
        const metadata = this.dataSource.getMetadata(entityClassOrName)
        const query = this.replaceObjectIdProperty(
            metadata,
            this.convertFindManyOptionsOrConditionsToMongodbQuery(
                optionsOrConditions,
            ),
        )
        const cursor = this.createEntityCursor<Entity>(entityClassOrName, query)
        const deleteDateColumn = metadata.deleteDateColumn

        if (FindOptionsUtils.isFindManyOptions(optionsOrConditions)) {
            if (optionsOrConditions.select)
                cursor.project(
                    this.convertFindOptionsSelectToProjectCriteria(
                        optionsOrConditions.select,
                        metadata,
                    ),
                )
            if (optionsOrConditions.skip) cursor.skip(optionsOrConditions.skip)
            if (optionsOrConditions.take) cursor.limit(optionsOrConditions.take)
            if (optionsOrConditions.order)
                cursor.sort(
                    this.convertFindOptionsOrderToOrderCriteria(
                        optionsOrConditions.order,
                    ),
                )
            if (deleteDateColumn && !optionsOrConditions.withDeleted) {
                this.filterSoftDeleted(cursor, deleteDateColumn, query)
            }
        } else if (deleteDateColumn) {
            this.filterSoftDeleted(cursor, deleteDateColumn, query)
        }
        return cursor.toArray()
    }

    /**
     * Finds entities that match given find options or conditions.
     *
     * @param entityClassOrName
     * @param optionsOrConditions
     */
    async executeFindAndCount<Entity>(
        entityClassOrName: EntityTarget<Entity>,
        optionsOrConditions?: MongoFindManyOptions<Entity> | Partial<Entity>,
    ): Promise<[Entity[], number]> {
        const metadata = this.dataSource.getMetadata(entityClassOrName)
        const query = this.replaceObjectIdProperty(
            metadata,
            this.convertFindManyOptionsOrConditionsToMongodbQuery(
                optionsOrConditions,
            ),
        )
        const cursor = this.createEntityCursor(entityClassOrName, query)
        const deleteDateColumn = metadata.deleteDateColumn

        if (FindOptionsUtils.isFindManyOptions(optionsOrConditions)) {
            if (optionsOrConditions.select)
                cursor.project(
                    this.convertFindOptionsSelectToProjectCriteria(
                        optionsOrConditions.select,
                        metadata,
                    ),
                )
            if (optionsOrConditions.skip) cursor.skip(optionsOrConditions.skip)
            if (optionsOrConditions.take) cursor.limit(optionsOrConditions.take)
            if (optionsOrConditions.order)
                cursor.sort(
                    this.convertFindOptionsOrderToOrderCriteria(
                        optionsOrConditions.order,
                    ),
                )
            if (deleteDateColumn && !optionsOrConditions.withDeleted) {
                this.filterSoftDeleted(cursor, deleteDateColumn, query)
            }
        } else if (deleteDateColumn) {
            this.filterSoftDeleted(cursor, deleteDateColumn, query)
        }
        const [results, count] = await Promise.all<any>([
            cursor.toArray(),
            this.count(entityClassOrName, query),
        ])
        return [results, parseInt(count)]
    }
}
